import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const ConcursosPage = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [concursos, setConcursos] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para filtros
  const [buscar, setBuscar] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('A');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [entidadFiltro, setEntidadFiltro] = useState('');
  const [soloActivos, setSoloActivos] = useState(true);
  
  // Estados para paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const elementosPorPagina = 15;
  
  // Estados para el modal
  const [showModal, setShowModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [concursoActual, setConcursoActual] = useState(() => ({
    id: null,
    id_entidad: '',
    descripcion: '',
    enlace: '',
    tipo_convocatoria: 'N',
    apertura: '',
    cierre: '',
    estado: 'A'
  }));

  // Estados para modal de entidades
  const [showModalEntidad, setShowModalEntidad] = useState(false);
  const [nuevaEntidad, setNuevaEntidad] = useState(() => ({ descripcion: '', estado: 'A' }));
  
  // Estados para gestión de entidades
  const [showModalGestionEntidades, setShowModalGestionEntidades] = useState(false);
  const [entidadEditar, setEntidadEditar] = useState(null);
  const [modoEdicionEntidad, setModoEdicionEntidad] = useState(false);

  useEffect(() => {
    setPaginaActual(1);
  }, [estadoFiltro, tipoFiltro, entidadFiltro, soloActivos, buscar]);

  useEffect(() => {
    cargarConcursos();
    cargarEntidades();
  }, [estadoFiltro, tipoFiltro, entidadFiltro, soloActivos, paginaActual, buscar]);

  const cargarConcursos = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        skip: (paginaActual - 1) * elementosPorPagina,
        limit: elementosPorPagina
      };
      
      if (estadoFiltro) params.estado = estadoFiltro;
      if (tipoFiltro) params.tipo_convocatoria = tipoFiltro;
      if (entidadFiltro) params.id_entidad = entidadFiltro;
      if (soloActivos) params.activos_solamente = true;
      if (buscar.trim()) params.search = buscar.trim();
      
      const response = await axios.get(`${API_BASE_URL}/api/concursos`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setConcursos(response.data);
    } catch (err) {
      console.error('Error al cargar concursos:', err);
      setError(err.response?.data?.detail || 'Error al cargar los concursos');
    } finally {
      setLoading(false);
    }
  };

  const cargarEntidades = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/entidades`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { estado: 'A' }
      });
      setEntidades(response.data);
    } catch (err) {
      console.error('Error al cargar entidades:', err);
    }
  };

  const abrirModalCrear = () => {
    setModoEdicion(false);
    setConcursoActual({
      id: null,
      id_entidad: '',
      descripcion: '',
      enlace: '',
      tipo_convocatoria: 'N',
      apertura: '',
      cierre: '',
      estado: 'A'
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const abrirModalEditar = (concurso) => {
    setModoEdicion(true);
    setConcursoActual({
      ...concurso,
      apertura: concurso.apertura || '',
      cierre: concurso.cierre || ''
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const cerrarModal = () => {
    setShowModal(false);
    setConcursoActual({
      id: null,
      id_entidad: '',
      descripcion: '',
      enlace: '',
      tipo_convocatoria: 'N',
      apertura: '',
      cierre: '',
      estado: 'A'
    });
    setError('');
    setSuccess('');
  };

  const guardarConcurso = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const payload = {
        id_entidad: parseInt(concursoActual.id_entidad),
        descripcion: concursoActual.descripcion,
        enlace: concursoActual.enlace,
        tipo_convocatoria: concursoActual.tipo_convocatoria,
        apertura: concursoActual.apertura || null,
        cierre: concursoActual.cierre || null,
        estado: concursoActual.estado
      };

      if (modoEdicion) {
        await axios.put(
          `${API_BASE_URL}/api/concursos/${concursoActual.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Concurso actualizado exitosamente');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/concursos`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Concurso creado exitosamente');
      }

      await cargarConcursos();
      setTimeout(() => {
        cerrarModal();
      }, 1500);
    } catch (err) {
      console.error('Error al guardar concurso:', err);
      setError(err.response?.data?.detail || 'Error al guardar el concurso');
    } finally {
      setLoading(false);
    }
  };

  const eliminarConcurso = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este concurso?')) return;

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/concursos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Concurso eliminado exitosamente');
      await cargarConcursos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error al eliminar concurso:', err);
      setError(err.response?.data?.detail || 'Error al eliminar el concurso');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const cerrarConcurso = async (id) => {
    if (!window.confirm('¿Está seguro de cerrar este concurso?')) return;

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/concursos/${id}/cerrar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Concurso cerrado exitosamente');
      await cargarConcursos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error al cerrar concurso:', err);
      setError(err.response?.data?.detail || 'Error al cerrar el concurso');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const crearEntidad = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      await axios.post(
        `${API_BASE_URL}/api/entidades`,
        nuevaEntidad,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Entidad creada exitosamente');
      await cargarEntidades();
      setShowModalEntidad(false);
      setNuevaEntidad({ descripcion: '', estado: 'A' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error al crear entidad:', err);
      setError(err.response?.data?.detail || 'Error al crear la entidad');
    }
  };

  const abrirModalEditarEntidad = (entidad) => {
    setEntidadEditar({ ...entidad });
    setModoEdicionEntidad(true);
  };

  const abrirModalNuevaEntidad = () => {
    setEntidadEditar({ descripcion: '', estado: 'A' });
    setModoEdicionEntidad(false);
  };

  const guardarEntidad = async () => {
    try {
      if (modoEdicionEntidad) {
        await axios.put(`${API_BASE_URL}/api/entidades/${entidadEditar.id}`, 
          { descripcion: entidadEditar.descripcion, estado: entidadEditar.estado },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Entidad actualizada exitosamente');
      } else {
        await axios.post(`${API_BASE_URL}/api/entidades`, entidadEditar, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Entidad creada exitosamente');
      }
      setEntidadEditar(null);
      setModoEdicionEntidad(false);
      await cargarEntidades();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar la entidad');
      setTimeout(() => setError(''), 3000);
    }
  };

  const eliminarEntidad = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta entidad? Esto afectará a todos los concursos asociados.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/entidades/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Entidad eliminada exitosamente');
      await cargarEntidades();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar la entidad');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-PE', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      'A': 'bg-success',
      'C': 'bg-secondary',
      'I': 'bg-danger'
    };
    const textos = {
      'A': 'Abierto',
      'C': 'Cerrado',
      'I': 'Inactivo'
    };
    return <span className={`badge ${badges[estado] || 'bg-secondary'}`}>{textos[estado] || estado}</span>;
  };

  const getTipoBadge = (tipo) => {
    return tipo === 'N' 
      ? <span className="badge bg-primary">Nacional</span>
      : <span className="badge bg-info">Internacional</span>;
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><i className="bi bi-trophy me-2"></i>{t('contests.title')}</h2>
        <div>
          <button className="btn btn-outline-info me-2" onClick={() => setShowModalGestionEntidades(true)}>
            <i className="bi bi-building-gear me-1"></i>
            {t('contests.entities.title')}
          </button>
          <button className="btn btn-outline-secondary me-2" onClick={() => setShowModalEntidad(true)}>
            <i className="bi bi-building me-1"></i>
            {t('contests.entities.create')}
          </button>
          <button className="btn btn-primary" onClick={abrirModalCrear}>
            <i className="bi bi-plus-circle me-1"></i>
            {t('contests.create')}
          </button>
        </div>
      </div>

      {/* Alertas */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle-fill me-2"></i>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Filtros */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">{t('common.search')}</label>
              <input
                type="text"
                className="form-control"
                placeholder={t('contests.search')}
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">{t('contests.filters.status')}</label>
              <select
                className="form-select"
                value={estadoFiltro}
                onChange={(e) => setEstadoFiltro(e.target.value)}
              >
                <option value="">{t('common.all')}</option>
                <option value="A">{t('contests.active')}</option>
                <option value="C">{t('contests.closed')}</option>
                <option value="I">{t('common.inactive')}</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">{t('contests.filters.type')}</label>
              <select
                className="form-select"
                value={tipoFiltro}
                onChange={(e) => setTipoFiltro(e.target.value)}
              >
                <option value="">{t('contests.filters.allTypes')}</option>
                <option value="N">{t('contests.fields.national')}</option>
                <option value="I">{t('contests.fields.international')}</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('contests.filters.entity')}</label>
              <select
                className="form-select"
                value={entidadFiltro}
                onChange={(e) => setEntidadFiltro(e.target.value)}
              >
                <option value="">{t('contests.filters.allEntities')}</option>
                {entidades.map(ent => (
                  <option key={ent.id} value={ent.id}>{ent.descripcion}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label d-block">&nbsp;</label>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={soloActivos}
                  onChange={(e) => setSoloActivos(e.target.checked)}
                  id="soloActivos"
                />
                <label className="form-check-label" htmlFor="soloActivos">
                  {t('contests.filters.onlyActive')}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de concursos */}
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
            </div>
          ) : concursos.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
              <p className="mt-3">{t('contests.noResults')}</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t('contests.fields.id')}</th>
                    <th>{t('contests.fields.entity')}</th>
                    <th>{t('contests.fields.description')}</th>
                    <th>{t('contests.fields.type')}</th>
                    <th>{t('contests.fields.openingDate')}</th>
                    <th>{t('contests.fields.closingDate')}</th>
                    <th>{t('common.status')}</th>
                    <th className="text-center">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {concursos.map((concurso) => (
                    <tr key={concurso.id}>
                      <td>{concurso.id}</td>
                      <td>
                        <i className="bi bi-building me-1 text-muted"></i>
                        {concurso.entidad_nombre || 'N/A'}
                      </td>
                      <td>
                        <div>{concurso.descripcion}</div>
                        {concurso.enlace && (
                          <small className="text-muted">
                            <a href={concurso.enlace} target="_blank" rel="noopener noreferrer">
                              <i className="bi bi-link-45deg me-1"></i>
                              {t('contests.fields.link')}
                            </a>
                          </small>
                        )}
                      </td>
                      <td>{getTipoBadge(concurso.tipo_convocatoria)}</td>
                      <td>{formatearFecha(concurso.apertura)}</td>
                      <td>{formatearFecha(concurso.cierre)}</td>
                      <td>{getEstadoBadge(concurso.estado)}</td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => abrirModalEditar(concurso)}
                            title={t('common.edit')}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          {concurso.estado === 'A' && (
                            <button
                              className="btn btn-outline-warning"
                              onClick={() => cerrarConcurso(concurso.id)}
                              title={t('common.close')}
                            >
                              <i className="bi bi-lock"></i>
                            </button>
                          )}
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => eliminarConcurso(concurso.id)}
                            title={t('common.delete')}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {concursos.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted small">
                {t('publishers.pagination.showing', { start: 1, end: concursos.length, total: concursos.length })}
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                  disabled={paginaActual === 1}
                >
                  <i className="bi bi-chevron-left"></i> {t('common.previous')}
                </button>
                <button className="btn btn-outline-primary btn-sm disabled">
                  {t('publishers.pagination.page', { current: paginaActual, total: 1 })}
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setPaginaActual(p => p + 1)}
                  disabled={concursos.length < elementosPorPagina}
                >
                  {t('common.next')} <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Concurso */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-trophy me-2"></i>
                  {modoEdicion ? t('contests.modal.editTitle') : t('contests.modal.createTitle')}
                </h5>
                <button type="button" className="btn-close" onClick={cerrarModal}></button>
              </div>
              <form onSubmit={guardarConcurso}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">{t('contests.fields.entity')} *</label>
                      <select
                        className="form-select"
                        value={concursoActual.id_entidad}
                        onChange={(e) => setConcursoActual({...concursoActual, id_entidad: e.target.value})}
                        required
                      >
                        <option value="">{t('journals.filters.selectPublisher')}</option>
                        {entidades.map(ent => (
                          <option key={ent.id} value={ent.id}>{ent.descripcion}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">{t('contests.fields.type')} *</label>
                      <select
                        className="form-select"
                        value={concursoActual.tipo_convocatoria}
                        onChange={(e) => setConcursoActual({...concursoActual, tipo_convocatoria: e.target.value})}
                        required
                      >
                        <option value="N">{t('contests.fields.national')}</option>
                        <option value="I">{t('contests.fields.international')}</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">{t('contests.fields.description')} *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={concursoActual.descripcion}
                        onChange={(e) => setConcursoActual({...concursoActual, descripcion: e.target.value})}
                        placeholder="Ej: Proyectos de Investigación Básica 2026-01"
                        maxLength={255}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">{t('contests.fields.link')} *</label>
                      <input
                        type="url"
                        className="form-control"
                        value={concursoActual.enlace}
                        onChange={(e) => setConcursoActual({...concursoActual, enlace: e.target.value})}
                        placeholder="https://ejemplo.com/convocatoria"
                        maxLength={255}
                        required
                      />
                      <small className="text-muted">URL con las bases y reglamentos del concurso</small>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('contests.fields.openingDate')}</label>
                      <input
                        type="date"
                        className="form-control"
                        value={concursoActual.apertura}
                        onChange={(e) => setConcursoActual({...concursoActual, apertura: e.target.value})}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('contests.fields.closingDate')}</label>
                      <input
                        type="date"
                        className="form-control"
                        value={concursoActual.cierre}
                        onChange={(e) => setConcursoActual({...concursoActual, cierre: e.target.value})}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('common.status')} *</label>
                      <select
                        className="form-select"
                        value={concursoActual.estado}
                        onChange={(e) => setConcursoActual({...concursoActual, estado: e.target.value})}
                        required
                      >
                        <option value="A">{t('contests.active')}</option>
                        <option value="C">{t('contests.closed')}</option>
                        <option value="I">{t('common.inactive')}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={cerrarModal}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span> {t('contests.modal.saving')}</>
                    ) : (
                      <><i className="bi bi-save me-1"></i> {t('common.save')}</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Nueva Entidad */}
      {showModalEntidad && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-building me-2"></i>
                  {t('contests.entities.create')}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModalEntidad(false)}></button>
              </div>
              <form onSubmit={crearEntidad}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">{t('contests.entities.name')} *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={nuevaEntidad.descripcion}
                      onChange={(e) => setNuevaEntidad({...nuevaEntidad, descripcion: e.target.value})}
                      placeholder="Ej: ProCiencia"
                      maxLength={255}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">{t('common.status')} *</label>
                    <select
                      className="form-select"
                      value={nuevaEntidad.estado}
                      onChange={(e) => setNuevaEntidad({...nuevaEntidad, estado: e.target.value})}
                      required
                    >
                      <option value="A">{t('common.active')}</option>
                      <option value="I">{t('common.inactive')}</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModalEntidad(false)}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-save me-1"></i>
                    {t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestión de Entidades */}
      {showModalGestionEntidades && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-building-gear me-2"></i>
                  {t('contests.entities.title')}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModalGestionEntidades(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <button className="btn btn-primary btn-sm" onClick={abrirModalNuevaEntidad}>
                    <i className="bi bi-plus-circle me-1"></i>
                    {t('contests.entities.create')}
                  </button>
                </div>
                
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>{t('contests.fields.id')}</th>
                        <th>{t('contests.entities.name')}</th>
                        <th>{t('common.status')}</th>
                        <th>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entidades.map(entidad => (
                        <tr key={entidad.id}>
                          <td>{entidad.id}</td>
                          <td>{entidad.descripcion}</td>
                          <td>
                            {entidad.estado === 'A' 
                              ? <span className="badge bg-success">{t('common.active')}</span>
                              : <span className="badge bg-secondary">{t('common.inactive')}</span>
                            }
                          </td>
                          <td>
                            <button 
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => abrirModalEditarEntidad(entidad)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => eliminarEntidad(entidad.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModalGestionEntidades(false)}>
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Entidad */}
      {entidadEditar && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-building me-2"></i>
                  {modoEdicionEntidad ? t('contests.entities.edit') : t('contests.entities.create')}
                </h5>
                <button type="button" className="btn-close" onClick={() => setEntidadEditar(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">{t('contests.entities.name')} *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={entidadEditar.descripcion}
                    onChange={(e) => setEntidadEditar({...entidadEditar, descripcion: e.target.value})}
                    placeholder="Ej: ProCiencia"
                    maxLength={255}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t('common.status')} *</label>
                  <select
                    className="form-select"
                    value={entidadEditar.estado}
                    onChange={(e) => setEntidadEditar({...entidadEditar, estado: e.target.value})}
                    required
                  >
                    <option value="A">{t('common.active')}</option>
                    <option value="I">{t('common.inactive')}</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEntidadEditar(null)}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn btn-primary" onClick={guardarEntidad}>
                  <i className="bi bi-save me-1"></i>
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcursosPage;
